// Downloads a json blob as a file by
// creating an anchor element and clicking it.
export const downloadJsonFile = (data: JSON, fileName: string) => {
  const blob = new Blob([JSON.stringify(data)], { type: "application/json" });

  const anchorElement = document.createElement("a");
  anchorElement.download = fileName;
  anchorElement.href = window.URL.createObjectURL(blob);

  anchorElement.click();

  anchorElement.remove();
};

export interface CSVData {
  headers: (string | number)[];
  rows: (string | number | null)[][];
}

export type CSVExportResultType = { csvData: CSVData; filename: string };

/**
 * Escapes a string for CSV format.
 * Doubles double quotes (CSV standard) and wraps in quotes if needed.
 * Also wraps values with single quotes to prevent parsing issues.
 */
const escapeCSVValue = (value: string): string => {
  // Double any existing double quotes (CSV standard: " becomes "")
  const escaped = value.replace(/"/g, '""');

  // If the value contains double quotes, single quotes, commas, or newlines, wrap it in quotes
  if (
    value.includes('"') ||
    value.includes("'") ||
    value.includes(",") ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${escaped}"`;
  }
  return value;
};

/**
 * Converts CSV data to a CSV string format.
 * Handles null values and string escaping for CSV format.
 */
const csvDataToString = (data: CSVData): string => {
  const csvRows = [data.headers, ...data.rows];
  return csvRows
    .map((row) =>
      row.map((cell) => (cell == null ? "" : escapeCSVValue(cell.toString()))).join(","),
    )
    .join("\n");
};

/**
 * Creates a CSV blob URL from data with headers and rows.
 * Adds a UTF-8 BOM for proper Excel compatibility with Unicode characters.
 */
export const csvObjUrlFromData = (data: CSVData): string => {
  const csvString = csvDataToString(data);

  // Pass BOM and CSV as separate parts - Blob handles concatenation efficiently
  const blob = new Blob(["\uFEFF", csvString], { type: "text/csv;charset=utf-8" });
  const objUrl = URL.createObjectURL(blob);

  return objUrl;
};

/**
 * Downloads a CSV file from CSV data and filename.
 * This will add a UTF-8 BOM for proper Excel compatibility with Unicode characters.
 * Handles the common pattern of creating a download link and cleaning up.
 */
export const downloadCSVFile = (csvData: CSVData, filename: string): void => {
  const csvUrl = csvObjUrlFromData(csvData);
  const link = document.createElement("a");
  link.href = csvUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(csvUrl);
};

/**
 * Formats a string into a safe CSV filename with current timestamp.
 */
export const formatCSVFilename = (name: string): string => {
  const filenameSafeName = name
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .toLowerCase();

  const dateString = new Date().toISOString();

  return `${filenameSafeName}_${dateString}.csv`;
};

/**
 * Formats a date range for use in filenames.
 * Returns format: e.g. Sep01-2025-Sep08-2025
 */
export const formatDateRangeForFilename = (startDate: string, endDate: string): string => {
  const formatDatePart = (date: string) => {
    const d = new Date(date);
    const month = d.toLocaleString("en-US", { month: "short" });
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}${day}-${year}`;
  };

  return `${formatDatePart(startDate)}-${formatDatePart(endDate)}`;
};

/**
 * Creates a safe filename with term and date range.
 * Returns format: <prefix>-<safeTerm>(<dateRange>).csv
 */
export const createSafeFilename = (
  prefix: string,
  term: string,
  dateRange: { startDate: string; endDate: string },
): string => {
  const safeTerm = term.replace(/[^a-zA-Z0-9]/g, "");
  const dateRangePart = formatDateRangeForFilename(dateRange.startDate, dateRange.endDate);
  return `${prefix}-${safeTerm}(${dateRangePart}).csv`;
};

// CSV formatting utility functions
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString();
};

export const formatDateISO = (dateString: string): string => {
  if (!dateString) return "";
  // Format as "2025-05-19 14:04:02+00:00" or similar
  return new Date(dateString).toISOString().replace("T", " ").replace("Z", "+00:00");
};

export const formatPercentage = (value: number): string => {
  return `${value}%`;
};

export const formatPoints = (points: number | null): string => {
  return points !== null ? points.toString() : "";
};

export const formatGroupWork = (isGroupWork: boolean): string => {
  return isGroupWork ? "true" : "false";
};

export const formatComment = (comment: string | null): string => {
  return comment || "";
};

export const formatOutcomes = (
  outcomes: Array<{ outcome_title: string; outcome_id: number; avg_score: number }>,
): string => {
  if (!outcomes || outcomes.length === 0) return "-";
  return outcomes.map((o) => `${o.outcome_title}: ${o.avg_score}`).join("; ");
};
